import '@mescius/spread-sheets/styles/gc.spread.sheets.excel2013white.css';
import '@/styles/spreadjs-overrides.css';

import type { SheetSpec } from '@meridian-ssa/shared';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import { logDebug, logError, logWarn } from '@/lib/logger';
import { getSpreadJS, loadSpreadJS } from '@/lib/spreadjs/loader';
import {
  addNamedRanges,
  applyCellStyle,
  applyConditionalFormats,
  parseA1,
  restoreOriginalState,
  setCellContent,
  toggleInvalidFormulaDisplay,
} from '@/lib/spreadjs/utils';

import { EngineWarningIcon } from './EngineWarningIcon';
import { ResetButton } from './ResetButton';

interface SpreadJSViewerProps {
  sheetJson: SheetSpec;
  computedValues?: any[][];
  height?: number | string;
  showFormulaBar?: boolean;
  scrollId?: string;
  engineChecks?: any;
  showExpandButton?: boolean;
}

interface ExpandedSpreadJSViewerProps {
  setIsExpanded: React.Dispatch<React.SetStateAction<boolean>>;
  sheetJson: SheetSpec;
  computedValues?: any[][];
  showFormulaBar: boolean;
  engineChecks?: any;
}

type ViewMode = 'values' | 'formulas';

export function SpreadJSViewer({
  sheetJson,
  computedValues,
  height,
  showFormulaBar = true,
  scrollId,
  engineChecks,
  showExpandButton = true,
}: SpreadJSViewerProps) {
  const workbookRef = useRef<any>(null);
  const isInitializedRef = useRef(false);
  const [viewMode, setViewMode] = useState<ViewMode>('values');
  const [isExpanded, setIsExpanded] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [SpreadSheets, setSpreadSheets] = useState<any>(null);

  // Reset handler to restore original spreadsheet state
  const handleReset = useCallback(() => {
    if (workbookRef.current) {
      const originalSnapshot = (workbookRef.current as any).originalSnapshot;
      if (originalSnapshot) {
        restoreOriginalState(workbookRef.current, originalSnapshot);
      }
    }
  }, []);

  // Load SpreadJS React component on mount
  React.useEffect(() => {
    if (typeof window === 'undefined') return;

    (async () => {
      try {
        // Load SpreadJS singleton (returns immediately if already loaded)
        await loadSpreadJS();

        // Load React wrapper
        const reactModule = await import('@mescius/spread-sheets-react');
        setSpreadSheets(() => reactModule.SpreadSheets);
      } catch (error) {
        logError('Failed to load SpreadJS', error);
      }
    })();
  }, []);

  // Callback when workbook is initialized by React component
  const handleWorkbookInitialized = (spread: any) => {
    // Guard against double initialization (React 18 Strict Mode)
    if (isInitializedRef.current) {
      logDebug('SpreadJS already initialized, skipping');
      return;
    }

    const GC = getSpreadJS();
    if (!GC) return;

    workbookRef.current = spread;

    // Defer initialization to next tick to allow modal to render first
    setTimeout(() => {
      if (!GC || !workbookRef.current) return;

      // Double-check inside timeout too
      if (isInitializedRef.current) return;

      // Mark as initialized BEFORE doing the work
      isInitializedRef.current = true;

      // Configure workbook
      spread.options.showFormulaBar = showFormulaBar;
      spread.options.scrollByPixel = true;
      // Increase tab strip ratio to show more sheet tabs (default is 0.6)
      // 0.9 means 90% for tabs, 10% for horizontal scrollbar
      spread.options.tabStripRatio = 0.75;

      // Batch repaints and recalculations to one operation after all calls
      spread.suspendPaint();
      spread.suspendEvent();
      spread.suspendCalcService();

      try {
        // Clear default sheets
        spread.clearSheets();

        // IMPORTANT: Create all sheets first BEFORE populating cells
        // This prevents #REF! errors when formulas reference sheets that haven't been created yet
        const sheets: any[] = [];
        sheetJson.sheets.forEach((sheetData, sheetIndex) => {
          const sheet = new GC.Spread.Sheets.Worksheet(sheetData.name);
          spread.addSheet(sheetIndex, sheet);
          // Keep sheets unprotected to allow editing
          sheet.options.isProtected = false;
          sheets.push(sheet);
        });

        // Now populate cells in all sheets (after all sheets exist)
        sheetJson.sheets.forEach((sheetData, sheetIndex) => {
          const sheet = sheets[sheetIndex];

          // Load cells
          sheetData.cells.forEach(cell => {
            const { row, col } = parseA1(cell.a1);

            // Set cell content using utility function
            setCellContent(sheet, cell, row, col);

            // Apply cell styling using utility function
            applyCellStyle(sheet, cell, row, col);
          });

          // Apply named ranges if present
          if (sheetData.namedRanges) {
            addNamedRanges(spread, sheetData.namedRanges, sheetData.name);
          }

          // Apply conditional formatting if present (SheetSpec@2)
          if (sheetData.conditionalFormats) {
            applyConditionalFormats(sheet, sheetData.conditionalFormats);
          }

          // Auto-fit columns and rows - calculate max col and row from cells
          let maxCol = 0;
          let maxRow = 0;
          sheetData.cells.forEach(cell => {
            try {
              const { row, col } = parseA1(cell.a1);
              maxCol = Math.max(maxCol, col);
              maxRow = Math.max(maxRow, row);
            } catch (e) {
              // Skip invalid cells
            }
          });

          // Auto-fit all columns to ensure proper width for content
          for (let i = 0; i <= maxCol; i++) {
            sheet.autoFitColumn(i);
          }

          // Auto-fit all rows to ensure proper height for content
          for (let i = 0; i <= maxRow; i++) {
            sheet.autoFitRow(i);
          }

          // Set default zoom level (0.85 = 85%)
          sheet.zoom(0.85);
        });

        // Store original snapshot for reset functionality (editable by default)
        const snapshot = spread.toJSON();
        (spread as any).originalSnapshot = snapshot;

        setIsLoaded(true);
      } catch (error) {
        // Silently handle SpreadJS initialization errors
        // These can occur due to canvas context timing issues but don't affect functionality
        isInitializedRef.current = false;
      } finally {
        // Batch resume recalculations and repaints
        spread.resumeCalcService();
        spread.resumeEvent();
        spread.resumePaint();
      }
    }, 0); // Defer to next tick
  };

  // Handle view mode changes
  React.useEffect(() => {
    const GC = getSpreadJS();
    if (!workbookRef.current || !isLoaded || !GC) return;

    try {
      const workbook = workbookRef.current;
      const activeSheet = workbook.getActiveSheet();

      if (!activeSheet) return;

      // Toggle between showing values and formulas
      activeSheet.options.showFormulas = viewMode === 'formulas';

      // Toggle invalid formula display (uses tracked list, no scanning needed)
      toggleInvalidFormulaDisplay(activeSheet, viewMode === 'formulas');
    } catch (e) {
      logWarn('Error changing view mode');
    }
  }, [viewMode, isLoaded]);

  if (!SpreadSheets) {
    return (
      <div
        style={{
          padding: '2rem',
          textAlign: 'center',
          color: '#8B7764',
          height: height ? `${height}px` : '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        Loading Spreadsheet...
      </div>
    );
  }

  return (
    <>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          height: height ? `${height}px` : '100%',
          minHeight: 0,
        }}
      >
        {/* Controls */}
        <div
          style={{
            display: 'flex',
            gap: '0.5rem',
            marginBottom: '0.5rem',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexShrink: 0,
          }}
        >
          <div
            style={{ display: 'flex', gap: '0.375rem', alignItems: 'center' }}
          >
            <button
              onClick={() => setViewMode('values')}
              type="button"
              style={{
                padding: '0.375rem 0.75rem',
                fontSize: '0.75rem',
                border: '1px solid #D1C9C4',
                background:
                  viewMode === 'values' ? 'rgba(183, 52, 58, 0.1)' : 'white',
                color: viewMode === 'values' ? '#B7343A' : '#8B7764',
                borderRadius: '6px',
                cursor: 'pointer',
                fontWeight: 500,
                transition: 'all 0.15s ease',
              }}
            >
              Values
            </button>
            <button
              onClick={() => setViewMode('formulas')}
              type="button"
              style={{
                padding: '0.375rem 0.75rem',
                fontSize: '0.75rem',
                border: '1px solid #D1C9C4',
                background:
                  viewMode === 'formulas' ? 'rgba(183, 52, 58, 0.1)' : 'white',
                color: viewMode === 'formulas' ? '#B7343A' : '#8B7764',
                borderRadius: '6px',
                cursor: 'pointer',
                fontWeight: 500,
                transition: 'all 0.15s ease',
              }}
            >
              Formulas
            </button>
            <ResetButton onReset={handleReset} isEnabled={isLoaded} />
            <EngineWarningIcon engineChecks={engineChecks} />
          </div>

          <div
            style={{ display: 'flex', gap: '0.375rem', alignItems: 'center' }}
          >
            {showExpandButton && (
              <button
                onClick={() => setIsExpanded(true)}
                type="button"
                style={{
                  padding: '0.375rem 0.75rem',
                  fontSize: '0.75rem',
                  border: '1px solid #D1C9C4',
                  background: 'white',
                  color: '#8B7764',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: 500,
                  transition: 'all 0.15s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.375rem',
                }}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M3.5 8.5L1 11V15H5L7.5 12.5M8.5 7.5L11 5H15V1L12.5 3.5M1 6V1H6M10 15H15V10"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                Expand
              </button>
            )}
          </div>
        </div>

        {/* SpreadJS Host - Using official React component */}
        <div
          style={{
            flex: 1,
            minHeight: 0,
            minWidth: 0,
            overflow: 'hidden',
            position: 'relative',
          }}
        >
          <div
            data-scroll-id={scrollId}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              border: '1px solid #D1C9C4',
              borderRadius: '6px',
              background: 'white',
              overflow: 'hidden',
            }}
          >
            <SpreadSheets
              key={JSON.stringify(sheetJson.sheets.map(s => s.name)).substring(
                0,
                50
              )}
              workbookInitialized={handleWorkbookInitialized}
              hostStyle={{
                width: '100%',
                height: '100%',
                padding: 0,
              }}
            />
          </div>
        </div>

        {/* Hint */}
        <p
          style={{
            marginTop: '0.375rem',
            marginBottom: 0,
            fontSize: '0.6875rem',
            color: '#8B7764',
            flexShrink: 0,
          }}
        >
          Editable mode - Double-click cells to edit • Press Reset to restore
          original
        </p>
      </div>

      {/* Expanded Modal */}
      {isExpanded && (
        <ExpandedSpreadJSViewer
          setIsExpanded={setIsExpanded}
          sheetJson={sheetJson}
          computedValues={computedValues}
          showFormulaBar={showFormulaBar}
          engineChecks={engineChecks}
        />
      )}
    </>
  );
}

const ExpandedSpreadJSViewer = ({
  setIsExpanded,
  sheetJson,
  computedValues,
  showFormulaBar,
  engineChecks,
}: ExpandedSpreadJSViewerProps) => {
  // Add escape key handler to close modal
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsExpanded(false);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [setIsExpanded]);

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        zIndex: 1000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
      onClick={() => setIsExpanded(false)}
    >
      <div
        style={{
          background: 'white',
          borderRadius: '12px',
          width: '90vw',
          height: '90vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Modal header */}
        <div
          style={{
            padding: '1rem 1.5rem',
            borderBottom: '1px solid #D1C9C4',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <h3
            style={{
              margin: 0,
              color: '#47331F',
              fontSize: '1.125rem',
              fontWeight: 500,
            }}
          >
            Spreadsheet View
          </h3>
          <button
            onClick={() => setIsExpanded(false)}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '1.5rem',
              cursor: 'pointer',
              color: '#8B7764',
              padding: '0.25rem',
              lineHeight: 1,
            }}
          >
            ×
          </button>
        </div>

        {/* Modal content - create new SpreadJS instance for expanded view */}
        <div style={{ flex: 1, overflow: 'hidden', padding: '1.5rem' }}>
          <SpreadJSViewer
            sheetJson={sheetJson}
            computedValues={computedValues}
            showFormulaBar={showFormulaBar}
            engineChecks={engineChecks}
            showExpandButton={false}
          />
        </div>
      </div>
    </div>
  );
};
